import { Paged } from './paged';

/** The list tabs, in tab order; mirrors IntakeMailController@index `?filter=` (D1 Task 1). */
export const INTAKE_FILTERS = ['needs_action', 'direct', 'not_a_role', 'duplicates', 'all'] as const;
export type IntakeFilter = typeof INTAKE_FILTERS[number];

/** Mirrors IntakeMail VERDICT_* constants (contract 3.1). */
export type IntakeVerdict =
  'pending' | 'direct_request' | 'not_a_role' | 'duplicate' | 'update_candidate' | 'held' | 'system' | 'expired';
/** Mirrors IntakeMail HELD_* constants. */
export type IntakeHeldReason = 'over_cap' | 'burst' | 'parser_unavailable' | 'ambiguous_match' | 'ambiguous_verdict';
export type IntakeUpdateState = 'applied' | 'ignored';
export type IntakeWipeState = 'none' | 'pending' | 'failed' | 'wiped';
/** 409 vacancy_in_use reasons (D1 Task 5). */
export type IntakeInUseReason = 'project' | 'opportunity' | 'submission' | 'interest';

/** The linked vacancy on a list item or detail (D1 Task 1). */
export interface IntakeVacancyRef {
  id: number;
  title: string;
  needs_details: boolean;
  buyer_Client_id: number | null;
  buyer_name: string | null;
}

/** A stored attachment (FileEntry with IntakeMail_id). Download only, never rendered. */
export interface IntakeAttachment {
  id: number;
  display_name: string;
  size: number;
  type: string;
}

/** One entry of `notes` (IntakeMail.attachment_notes); codes in contract 3.6. */
export interface IntakeNote {
  code: string;
  index?: number;
  field?: string;
}

export interface IntakeContact {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
}

/** `fields`: what the parser extracted, without `_` keys and without contacts. */
export interface IntakeExtractedFields {
  title?: string | null;
  company?: string | null;
  deadline?: string | null;
  start_date?: string | null;
  reference?: string | null;
  url?: string | null;
  hours_per_week?: string | number | null;
  tariff?: string | number | null;
  location?: string | null;
  description?: string | null;
}

export interface IntakeMatchCandidate {
  id: number;
  title: string;
}

/** One row of `update_diff`; `old` is the stored value (dates Y-m-d), `new` the mail's. */
export interface IntakeDiffRow {
  field: string;
  old: string | number | null;
  new: string | number | null;
}

/** One row of the AI decision log (D1 IntakeMailResource). */
export interface IntakeMailEvent {
  id: number;
  event: string;
  verdict: IntakeVerdict | null;
  held_reason: IntakeHeldReason | null;
  confidence: number | null;
  model: string | null;
  outcome: string | null;
  latency_ms: number | null;
  prompt_version: number | null;
  actor_name: string | null;
  created_at: string;
}

/** D1 IntakeMailResource::listItem. */
export interface IntakeMailListItem {
  id: number;
  received_at: string;
  verdict: IntakeVerdict;
  original_verdict: IntakeVerdict | null;
  held_reason: IntakeHeldReason | null;
  update_state: IntakeUpdateState | null;
  parse_state: 'pending' | 'parsing' | 'decided';
  content_wipe_state: IntakeWipeState;
  confidence: number | null;
  overridden_at: string | null;
  /** Null unless content_wipe_state is none. */
  sender: string | null;
  subject: string | null;
  vacancy: IntakeVacancyRef | null;
}

/** D1 IntakeMailResource::detail. */
export interface IntakeMail extends IntakeMailListItem {
  forwarder: string | null;
  parser_reason: string | null;
  notes: IntakeNote[];
  has_html: boolean;
  /** The text part, only when there is no HTML part; null once a wipe starts. */
  body_text: string | null;
  /** PHP serializes an empty array as `[]`; read it through intakeFields(). */
  fields: IntakeExtractedFields | [];
  contacts: IntakeContact[];
  attachments: IntakeAttachment[];
  match_candidates: IntakeMatchCandidate[];
  update_diff: IntakeDiffRow[];
  events: IntakeMailEvent[];
  /** Set only for held over_cap. */
  usage: { used: number; cap: number } | null;
  content_removed_on: string | null;
}

/** D1 list response: `{data, meta}`. `counts` ignores q. */
export interface IntakeMailListResponse {
  data: IntakeMailListItem[];
  meta: { total: number; per_page: number; current_page: number; last_page: number };
  counts: Record<IntakeFilter, number>;
}

export interface IntakeAddress {
  /** Both null when the tenant has no active address yet. */
  address: string | null;
  masked: string | null;
  retention_days: number;
  monthly_cap: number;
}

export interface IntakeBuyer {
  id: number;
  name: string;
  status: number;
  crm_type: string | null;
}

/** Body of POST .../direct-request (no buyer_Client_id) and PUT .../vacancy (D1 Task 4 allowlist). */
export interface IntakeVacancyFields {
  title: string;
  company?: string | null;
  location?: string | null;
  description?: string | null;
  url?: string | null;
  deadline?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  hours_per_week?: string | null;
  contract_duration?: string | null;
  tariff?: string | null;
  industry?: string | null;
  buyer_Client_id?: number | null;
}

/** 200 body of DELETE intake/mails/{mail} (D1 Task 13). */
export interface IntakeDeleteResult {
  content_wipe_state: IntakeWipeState;
}

/** Paged<T> for the dual-axis delegate, built from the D1 `{data, meta}` list. */
export function intakePaged(res: Pick<IntakeMailListResponse, 'data' | 'meta'>): Paged<IntakeMailListItem> {
  const data = res?.data ?? [];
  const meta = res?.meta ?? { total: 0, per_page: data.length, current_page: 1, last_page: 1 };
  const from = data.length ? (meta.current_page - 1) * meta.per_page + 1 : 0;
  return {
    current_page: meta.current_page, data, from, last_page: meta.last_page,
    per_page: meta.per_page, to: data.length ? from + data.length - 1 : 0, total: meta.total,
  };
}

/** `fields` as an object, also when PHP sent an empty array. */
export function intakeFields(mail: { fields?: IntakeExtractedFields | [] | null } | null): IntakeExtractedFields {
  const f = mail?.fields;
  return !f || Array.isArray(f) ? {} : f;
}
